// components/MarkdownRenderer.js
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import ReactPlayer from "react-player";
import { classMap } from "../markdown/classMap";
import { remarkVideo } from "../markdown/remarkVideo";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

const MarkdownRenderer = ({ markdown, className, images = {} }) => {
	// Helper function to check if a URL is a YouTube URL
	const isYouTubeUrl = (url) => {
		if (!url) return false;
		return /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/i.test(
			url
		);
	};

	// Process video shortcodes in markdown before rendering
	const processVideoShortcodes = (content) => {
		// Match [video src="..." width="..."] syntax (simplified)
		const videoRegex = /\[video\s+src="([^"]+)"(?:\s+width="([^"]*)")?\]/g;

		return content.replace(videoRegex, (match, src, width) => {
			// Check if src matches a key in the images object, otherwise use as-is
			const videoSrc = images[src] || src;
			const videoWidth = width || "600";

			// Return HTML video element that will be processed by rehypeRaw
			return `<video controls width="${videoWidth}" class="w-full h-auto object-contain my-8 lg:my-12 max-h-[300px] lg:max-h-[500px] rounded-2xl mx-auto">
  <source src="${videoSrc}" type="video/mp4" />
  <source src="${videoSrc}" type="video/quicktime" />
  Your browser does not support the video tag.
</video>`;
		});
	};

	// Process the markdown content
	const processedMarkdown = processVideoShortcodes(markdown);

	// Enhanced classMap with image mapping support
	const enhancedClassMap = {
		...classMap,
		img: ({ node, className, src, alt, ...props }) => {
			// Check if src matches a key in the images object
			const imageSrc = images[src] || src;

			// Handle YouTube URLs
			if (isYouTubeUrl(imageSrc)) {
				return (
					<div className="aspect-video w-full my-8 lg:my-12">
						<ReactPlayer
							url={imageSrc}
							controls
							className="rounded-2xl mx-auto"
							width="100%"
							height="100%"
						/>
					</div>
				);
			}

			// Handle video files
			if (
				imageSrc &&
				(imageSrc.endsWith(".mp4") || imageSrc.endsWith(".mov"))
			) {
				return (
					<video
						src={imageSrc}
						controls
						className="w-full h-auto object-contain my-8 lg:my-12 max-h-[300px] lg:max-h-[500px] rounded-2xl mx-auto"
					/>
				);
			}

			return (
				<img
					src={imageSrc}
					alt={alt}
					className="w-full h-auto object-contain my-8 lg:my-12 max-h-[300px] lg:max-h-[500px] rounded-2xl mx-auto"
					{...props}
				/>
			);
		},
	};

	// Custom code renderer with Prism highlighting for fenced code blocks
	const CodeBlock = ({ inline, className, children, ...props }) => {
		const match = /language-(\w+)/.exec(className || "");
		const language = match ? match[1] : "javascript";
		if (inline) {
			return (
				<code
					className="rounded bg-gray-100 px-1 py-0.5 text-[0.95em]"
					{...props}
				>
					{children}
				</code>
			);
		}

		return (
			<div className="w-full max-w-full min-w-0 overflow-hidden my-6">
				<div className="w-full max-w-full min-w-0 overflow-x-auto bg-[#1e1e1e] rounded-xl border border-gray-800">
					<SyntaxHighlighter
						language={language}
						style={vscDarkPlus}
						customStyle={{
							margin: 0,
							padding: "1.25rem",
							background: "transparent",
							fontSize: "13px",
							lineHeight: "1.4",
							borderRadius: "0",
							border: "none",
							boxSizing: "border-box",
							width: "100%",
							maxWidth: "100%",
							minWidth: 0,
						}}
						showLineNumbers
						lineNumberStyle={{
							color: "#6e7681",
							paddingRight: "1rem",
							userSelect: "none",
							minWidth: "2.5rem",
						}}
						wrapLines={false}
						wrapLongLines={false}
					>
						{String(children).replace(/\n$/, "")}
					</SyntaxHighlighter>
				</div>
			</div>
		);
	};

	// Avoid double wrapping by neutralizing the default <pre> when we provide a block renderer
	const PreBlock = ({ children }) => <>{children}</>;

	return (
		<div className={className ?? "w-full"}>
			<ReactMarkdown
				rehypePlugins={[rehypeRaw]}
				remarkPlugins={[remarkGfm, remarkDirective, remarkVideo, remarkBreaks]}
				components={{
					...enhancedClassMap,
					code: CodeBlock,
					pre: PreBlock,
				}}
			>
				{processedMarkdown}
			</ReactMarkdown>
		</div>
	);
};

export default MarkdownRenderer;
